import React, { useState, useEffect } from "react";
import { MultiSelect } from "primereact/multiselect";
import { getAllNeighborhoods } from "../services/addressService";
import { getAllUsers } from "../services/userService";
import { getZiyaretciRaporlariOranli } from "../services/summaryService";
import { ArrowDownIcon } from "../assets/icons";

const Raporlama = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form inputs
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [selectedPersonel, setSelectedPersonel] = useState([]);
  
  // Options for dropdowns
  const [neighborhoodOptions, setNeighborhoodOptions] = useState([]);
  const [personelOptions, setPersonelOptions] = useState([]);
  
  // Loading states
  const [isNeighborhoodLoading, setIsNeighborhoodLoading] = useState(false);
  const [isPersonelLoading, setIsPersonelLoading] = useState(false);

  // Initialize with today's date
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    fetchNeighborhoods();
    fetchPersonel();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNeighborhoods = async () => {
    try {
      setIsNeighborhoodLoading(true);
      const response = await getAllNeighborhoods();
      
      // Response structure: { status: 200, message: "Success", data: [{id: "...", ad: "..."}] }
      const neighborhoods = response?.data?.data || response?.data || [];
      
      const normalized = neighborhoods
        .map((item) => ({
          id: String(item.id || ""),
          name: String(item.ad || ""),
        }))
        .filter((item) => item.id && item.name);
      
      setNeighborhoodOptions(normalized);
    } catch (error) {
      console.error("Failed to fetch neighborhoods:", error);
      setNeighborhoodOptions([]);
    } finally {
      setIsNeighborhoodLoading(false);
    }
  };

  const fetchPersonel = async () => {
    try {
      setIsPersonelLoading(true);
      const users = await getAllUsers();
      // Filter only active users and normalize efficiently
      const normalized = users
        .filter((user) => user.isActive !== false)
        .map((user) => ({
          id: String(user.id ?? user.Id ?? ""),
          name: `${user.name ?? ""} ${user.surname ?? ""}`.trim() || user.email || String(user.id ?? ""),
        }))
        .filter((user) => user.id && user.name);
      setPersonelOptions(normalized);
    } catch (error) {
      console.error("Failed to fetch personel:", error);
      setPersonelOptions([]);
    } finally {
      setIsPersonelLoading(false);
    }
  };

  const handleNeighborhoodChange = (e) => {
    setSelectedNeighborhoods(e.value || []);
  };

  const handlePersonelChange = (e) => {
    setSelectedPersonel(e.value || []);
  };

  const handleDownloadReport = async () => {
    if (!startDate || !endDate) {
      setError("Başlangıç ve bitiş tarihleri zorunludur");
      return;
    }

    // Validate date range
    if (new Date(startDate) > new Date(endDate)) {
      setError("Başlangıç tarihi bitiş tarihinden sonra olamaz");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const params = {
        basla: startDate,
        bitir: endDate,
      };

      // Add optional parameters
      if (selectedNeighborhoods.length > 0) {
        params.mah = selectedNeighborhoods.map((n) => n.id).join(",");
      }

      if (selectedPersonel.length > 0) {
        params.personelids = selectedPersonel.map((p) => p.id).join(",");
      }

      const response = await getZiyaretciRaporlariOranli(params);
      const blob = response.data;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const today = new Date().toLocaleDateString("tr-TR").replace(/\./g, "-");
      link.href = url;
      link.download = `ziyaretci-raporlari-oranli-${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download report:", error);
      setError("Rapor indirme işlemi başarısız oldu");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="people-container">
      <h1 className="page-title">Raporlama</h1>

      {error && <div className="error-message">{error}</div>}

      <div className="search-filter-section">
        <div className="filter-container summary-filter-container">
          <div style={{ display: "flex", flexDirection: "column", gap: "15px"}}>
            {/* Date inputs */}
            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label htmlFor="start-date" style={{ fontSize: "14px", fontWeight: "500" }}>
                  Başlangıç Tarihi *
                </label>
                <input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setError(null);
                  }}
                  max={endDate || undefined}
                  className="p-inputtext p-component date-input"
                  style={{ height: "43px", padding: "0 10px", border: '1px solid #ddd' }}
                  required
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label htmlFor="end-date" style={{ fontSize: "14px", fontWeight: "500" }}>
                  Bitiş Tarihi *
                </label>
                <input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setError(null);
                  }}
                  min={startDate || undefined}
                  max={new Date().toISOString().split("T")[0]}
                  className="p-inputtext p-component date-input"
                  style={{ height: "43px", padding: "0 10px", border: '1px solid #ddd' }}
                  required
                />
              </div>
            </div>

            {/* Neighborhood multiselect */}
            <div className="visit-filter-group mahalle-group">
              <label htmlFor="neighborhood-select">Mahalle</label>
              <MultiSelect
                id="neighborhood-select"
                value={selectedNeighborhoods}
                options={neighborhoodOptions}
                onChange={handleNeighborhoodChange}
                optionLabel="name"
                placeholder="Mahalle Seçiniz"
                className="mahalle-multiselect"
                disabled={isNeighborhoodLoading || neighborhoodOptions.length === 0}
                filter
                display="chip"
                maxSelectedLabels={3}
                appendTo="self"
              />
              {isNeighborhoodLoading && <p className="modal-helper-text">Mahalleler yükleniyor...</p>}
              {selectedNeighborhoods.length > 0 && (
                <p className="modal-helper-text">{selectedNeighborhoods.length} mahalle seçildi.</p>
              )}
            </div>

            {/* Personel multiselect */}
            <div className="visit-filter-group mahalle-group">
              <label htmlFor="personel-select">Personel</label>
              <MultiSelect
                id="personel-select"
                value={selectedPersonel}
                options={personelOptions}
                onChange={handlePersonelChange}
                optionLabel="name"
                placeholder="Personel Seçiniz"
                className="mahalle-multiselect"
                disabled={isPersonelLoading || personelOptions.length === 0}
                filter
                display="chip"
                maxSelectedLabels={3}
                virtualScrollerOptions={{ itemSize: 38 }}
              />
              {isPersonelLoading && <p className="modal-helper-text">Personel yükleniyor...</p>}
              {selectedPersonel.length > 0 && (
                <p className="modal-helper-text">
                  {selectedPersonel.length} personel seçildi: {selectedPersonel.map(p => p.name).join(", ")}
                </p>
              )}
            </div>

            {/* Download button */}
            <button
              onClick={handleDownloadReport}
              className="download-excel-button"
              disabled={isLoading || !startDate || !endDate}
              style={{
                cursor: isLoading || !startDate || !endDate ? "not-allowed" : "pointer",
                opacity: isLoading || !startDate || !endDate ? 0.6 : 1,
              }}
            >
              <p>{isLoading ? "İndiriliyor..." : "Raporu İndir"}</p>
              {isLoading ? (
                <div className="spinner"></div>
              ) : (
                <ArrowDownIcon color="white" className="filter-icon" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Raporlama;
